import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  BffGetListUserServicesEndpoint,
  BffGetOneUserServiceEndpoint,
  UserService as UserServiceApi,
} from 'lingo2-api-models';
import { type IPagination, User, UserService, IPagedResults } from 'lingo2-models';
import { map, type Observable } from 'rxjs';

export type ListFilterForLigrary = Extract<BffGetListUserServicesEndpoint.Body, { strategy: 'library' }>['filter'];
export type ListFilterForProfile = Extract<BffGetListUserServicesEndpoint.Body, { strategy: 'profile' }>['filter'];
export type ListFilterOwned = Extract<BffGetListUserServicesEndpoint.Body, { strategy: 'owned' }>['filter'];

/** Маппер для приведения к старому типу UserService для обратной совместимости */
function mapToUserService(userService: UserServiceApi): UserService {
  const constructData: Partial<UserService> = { id: userService.id };

  //
  for (const key of Object.keys(userService)) {
    switch (key) {
      case 'author':
        constructData.author = new User({
          id: userService.author.id,
          slug: userService.author.slug,
          first_name: userService.author.first_name,
          last_name: userService.author.last_name,
          userpic_id: userService.author.userpic_id,
          userpic: userService.author.userpic,
          stats: {
            lessons_count: userService.author.stats.lessons_count,
            monthly_lessons_published: userService.author.stats.monthly_lessons_published,
            reviews_count: userService.author.stats.reviews_count,
            reviews_rating: userService.author.stats.reviews_rating,
          },
        });
        break;
      case 'teachers':
        constructData.teachers = userService.teachers.map(
          (teacher) =>
            new User({
              id: teacher.id,
              slug: teacher.slug,
              first_name: teacher.first_name,
              last_name: teacher.last_name,
              userpic_id: teacher.userpic_id,
              userpic: teacher.userpic,
            }),
        );
        break;
      // dates
      case 'created_at':
      case 'latest_applicable_date':
        constructData[key] = new Date(userService[key]);
        break;
      // Базовые поля
      default:
        constructData[key] = userService[key];
    }
  }

  return new UserService(constructData);
}

@Injectable({ providedIn: 'root' })
export class UserServicesBffService {
  private readonly http = inject(HttpClient);
  private baseUrl = (path: string): string => `${environment.bff_url}/${path}`;

  getOne(dto: BffGetOneUserServiceEndpoint.Body): Observable<UserService> {
    return this.http
      .post<BffGetOneUserServiceEndpoint.Response>(this.baseUrl(BffGetOneUserServiceEndpoint.path), dto, {
        observe: 'body',
      })
      .pipe(
        map((response) => {
          if (response.status === 'ok') {
            const userService = mapToUserService(response.data.item);
            return userService;
          } else {
            console.error(response.errorMessage);
            return null;
          }
        }),
      );
  }

  getList(dto: BffGetListUserServicesEndpoint.Body): Observable<{ list: UserService[]; pagination: IPagination }> {
    return this.http
      .post<BffGetListUserServicesEndpoint.Response>(this.baseUrl(BffGetListUserServicesEndpoint.path), dto, {
        observe: 'body',
      })
      .pipe(
        map((response) => {
          if (response.status === 'ok') {
            return {
              list: response.data.list.map(mapToUserService),
              pagination: response.data.pagination,
            };
          } else {
            console.error(response.errorMessage);
            return { list: [], pagination: null };
          }
        }),
      );
  }

  defaultGetList(
    dto: BffGetListUserServicesEndpoint.Body,
  ): Observable<Extract<BffGetListUserServicesEndpoint.Response, { status: 'ok' }>['data']> {
    return this.http
      .post<BffGetListUserServicesEndpoint.Response>(this.baseUrl(BffGetListUserServicesEndpoint.path), dto, {
        observe: 'body',
      })
      .pipe(
        map((response) => {
          if (response.status === 'ok') {
            return response.data;
          } else {
            console.error(response.errorMessage);
            return { list: [], pagination: null };
          }
        }),
      );
  }
}
